import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { isEmpty } from "lodash";
import { css, Theme } from "@emotion/react";
import { Link } from "react-router-dom";

import { getSiteTitle } from "core/config/config";
import { useAppState } from "core/contexts/AppContext";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import Loading from "core/Components/Utils/Loading";
import { hasFeatureFlag } from "core/includes/site";
import { FLAG_SHOP_2_WITH_EVENTS } from "core/includes/featureFlags";
import window from 'core/includes/window';
import useBreakpoint from "core/hooks/useBreakpoint";
import { analytics } from "core/includes/ga4EcommerceTracker";

import CheckoutButton from "pages/Components/CartAndCheckout/components/CheckoutButton";
import ConfirmationPopup from "pages/Components/CartAndCheckout/components/ConfirmationPopup";
import EmptyCart from "pages/Components/CartAndCheckout/components/EmptyCart";
import HeaderContainer from "pages/Components/CartAndCheckout/components/HeaderContainer";
import HeaderItemCount from "pages/Components/CartAndCheckout/components/HeaderItemCount";
import HeaderTitle from "pages/Components/CartAndCheckout/components/HeaderTitle";
import MainContainer from "pages/Components/CartAndCheckout/components/MainContainer";
import PageSection from "pages/Components/CartAndCheckout/components/PageSection";
import PageLeftSection from "pages/Components/CartAndCheckout/components/PageLeftSection";
import PageRightSection from "pages/Components/CartAndCheckout/components/PageRightSection";
import ItemGroupContainer from "pages/Components/CartAndCheckout/components/Item/ItemGroupContainer";
import ItemGroupSection from "pages/Components/CartAndCheckout/components/Item/ItemGroupSection";
import useCartDetailSizes from "pages/Components/CartAndCheckout/hooks/useCartDetailSizes";
import CartSummaryContainer from "pages/Components/CartAndCheckout/components/CartSummaryContainer";
import { isDown, isUp } from "theme/breakpoints";

const DEFAULT_PAGE_TITLE = 'Shopping Cart';

type Props = {
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
};

const CartPage: React.FC<Props> = ({ showBreadcrumb, hideHeaderCountry }) => {
    const [appState, setAppState] = useAppState();
    const { breakpoint } = useBreakpoint();
    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });
    const locationOrigin = window.location?.origin;
    const hasShopEventsFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_2_WITH_EVENTS);

    const {
        paddingTop,
        paddingRight,
        itemGroupBoxWidth,
        itemBoxWidth,
        itemBoxImageWidth,
        itemBoxImageHeight,
        itemBoxDescriptionWidth,
        itemBoxDescriptionLeftRightMargin,
        itemBoxPriceWidth,
        itemBoxPriceRightMargin,
        quantityBoxContainerWidth,
        quantityBoxWidth,
        quantityBoxHeight,
        plusMinusCircleButtonSize,
        isSmallBreakpoint,
    } = useCartDetailSizes();

    useEffect(() => {
        if (appState.currentPageName) {
            setAppState({ currentPageName: "" });
        }
    }, [appState.currentPageName, setAppState]);

    /**
     * Error message for discount code error
     * @param discountCode
     * @param applyTarget
     * @param errorMessage
     */
    const setValidationError = (discountCode: string, applyTarget: string, errorMessage: string = '') => {
        setAppState((prev: any) => ({
            cart: {
                ...prev.cart,
                discountCodeApplyTarget: applyTarget,
                validateDiscountCode: {
                    triedDiscountCode: discountCode,
                    errorMessage: errorMessage || 'The code is not available.'
                }
            }
        }));
    };

    const cartItems = useMemo(() => (
        appState.cart?.products || []
    ), [appState]);

    const groupedItems = useMemo(() => {
        if (!cartItems.length) return {};

        const products = cartItems.filter((item: CartProduct) => item.type === "product");
        const events = cartItems.filter((item: CartProduct) => item.type === "event");

        if (!hasShopEventsFeatureFlag) {
            return {
                product: products
            };
        }

        return {
            event: events,
            product: products
        };
    }, [cartItems, hasShopEventsFeatureFlag]);

    const cartStatus = useMemo(() => {
        if (isEmpty(groupedItems)) return "empty";
        if (hasShopEventsFeatureFlag) {
            if (groupedItems.event.length && groupedItems.product.length) return "both";
            if (groupedItems.event.length) return 'event';
        }
        if (groupedItems.product.length) return 'product';
    }, [groupedItems, hasShopEventsFeatureFlag]);

    // GA4 eCommerce
    useEffect(() => {
        if (appState.cart) {
            const isGlobal = appState.globalCurrencyOptions.isGlobal || appState.cart?.isGlobal;
            analytics.viewCart(
                appState.cart,
                isGlobal ? appState.globalCurrencyOptions.currency : null,
                isGlobal ? appState.globalCurrencyOptions.currencySymbol : null,
            );
        }
    }, [appState.cart]);

    const renderButtons = () => (
        <div css={buttonsContainer}>
            <CheckoutButton isClickable={cartItems.length > 0} />
            <Link to={"/classroom-resources"}>
                <button css={stepBackButtonStyle}>
                    Back to shop
                </button>
            </Link>
        </div>
    );

    if (appState.isCartLoading) {
        return (
            <Loading onTop fitToScreen overlay overlayColour={"white"} delay={500} />
        );
    }

    return (
        <>
            <Helmet>
                {locationOrigin && (
                    <link rel="canonical" href={locationOrigin} />
                )}
                <title>{getSiteTitle("Cart")}</title>
            </Helmet>
            <PageSection headerHeight={headerHeight}>
                <PageLeftSection isSmallBreakpoint={isSmallBreakpoint}>
                    <MainContainer paddingTop={paddingTop} paddingRight={paddingRight}
                                   itemGroupBoxWidth={itemGroupBoxWidth}>
                        <HeaderContainer>
                            <HeaderTitle>{DEFAULT_PAGE_TITLE}</HeaderTitle>
                            <HeaderItemCount itemCount={appState.cart?.products?.length || 0} />
                        </HeaderContainer>

                        {/* Cart doesn't have any items */}
                        {!cartItems.length && <EmptyCart />}

                        <ItemGroupSection cartStatus={cartStatus}>
                            {(!!cartItems.length && Object.keys(groupedItems)?.map(
                                (key: string, index: number) => (
                                    <ItemGroupContainer
                                        key={`${key}-${index}`}
                                        type={key}
                                        items={Object.values(groupedItems)[index]}
                                        groupStatus={cartStatus}
                                        itemGroupBoxWidth={itemGroupBoxWidth}
                                        itemBoxImageHeight={itemBoxImageHeight}
                                        itemBoxWidth={itemBoxWidth}
                                        itemBoxImageWidth={itemBoxImageWidth}
                                        itemBoxDescriptionWidth={itemBoxDescriptionWidth}
                                        itemBoxDescriptionLeftRightMargin={itemBoxDescriptionLeftRightMargin}
                                        itemBoxPriceWidth={itemBoxPriceWidth}
                                        itemBoxPriceRightMargin={itemBoxPriceRightMargin}
                                        quantityBoxContainerWidth={quantityBoxContainerWidth}
                                        quantityBoxWidth={quantityBoxWidth}
                                        quantityBoxHeight={quantityBoxHeight}
                                        plusMinusCircleButtonSize={plusMinusCircleButtonSize}
                                        setValidationError={setValidationError}
                                        isSmallBreakpoint={isSmallBreakpoint}
                                    />
                                )
                            ))}
                        </ItemGroupSection>
                    </MainContainer>
                </PageLeftSection>
                <PageRightSection paddingTop={paddingTop}>
                    {isDown(breakpoint, "md") && renderButtons()}
                    <CartSummaryContainer cartStatus={cartStatus}>
                        {isUp(breakpoint, "lg") && renderButtons()}
                    </CartSummaryContainer>
                </PageRightSection>

                {/* Confirmation popup for 'Remove all' */}
                <ConfirmationPopup />
            </PageSection>
        </>
    );
};

const stepBackButtonStyle = (theme: Theme) => css`
    ${theme.mixins.button.types.nice.simpleOutlinedBlue};

    width: 232px;
    height: 51px;
`;

const buttonsContainer = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    
    ${theme.breakpoints.down("md")} {
        padding-bottom: 40px;
    }
`;

export default CartPage;